import _ from 'lodash'
import MediaflowService from '@/services/MediaflowService'

/**
 * Sources dock actions shared by the workspace and the pop-out: reorder and
 * show / hide follow the desk's draft rule (staged on the on-air scene),
 * audio is a mixer and always saves at once. Other windows are told to
 * refresh after a direct save.
 */
export function useMediaflowSourceActions({ mediaflow, store, notify, desk }) {
	const flow = desk.editingFlow
	const audioBlocked = (media) => !media || desk.isTempId(media.id)
	const told = () => desk.post({ type: 'refresh' })

	const reorder = async (fromIndex, toIndex) => {
		if (!flow.value) return
		if (desk.stagesEdits.value) { desk.stageReorder(fromIndex, toIndex); return }
		const flowId = flow.value._id
		try {
			store.dispatch('Mediaflows/reorderFlowMedia', { mediaflowId: mediaflow.value._id, flowId, fromIndex, toIndex })
			await MediaflowService.reorderFlowMedia(mediaflow.value._id, flowId, fromIndex, toIndex)
			told()
		} catch (err) {
			console.error(err)
			notify({ group: 'error', text: 'Failed to update media order' })
		}
	}

	const toggleShow = async (media) => {
		if (!flow.value) return
		if (desk.stagesEdits.value) { desk.stageToggleShow(media.id); return }
		const mediaflowId = mediaflow.value._id
		const flowId = flow.value._id
		const before = media.show !== false
		const setShow = (show) => store.dispatch('Mediaflows/updateFlowMedia', { mediaflowId, flowId, mediaId: media.id, media: { show } })
		setShow(!before)
		try {
			await MediaflowService.toggleFlowMedia(mediaflowId, flowId, media.id)
			told()
		} catch (err) {
			setShow(before)
			console.error('Failed to toggle media visibility', err)
			notify({ group: 'error', text: 'Failed to toggle media show' })
		}
	}

	const saveVolume = _.debounce(async (mediaflowId, flowId, mediaId, volume) => {
		try {
			await MediaflowService.updateFlowMedia(mediaflowId, flowId, mediaId, { volume })
			told()
		} catch (err) {
			console.error(err)
			notify({ group: 'error', text: 'Failed to update volume' })
		}
	}, 500)

	const saveAudioSync = _.debounce(async (mediaflowId, flowId, mediaId, offset) => {
		try {
			await MediaflowService.setAudioSync(mediaflowId, flowId, mediaId, offset)
			store.dispatch('Mediaflows/updateFlowMediaAudioOffset', { mediaflowId, flowId, mediaId, audioOffset: offset })
			told()
		} catch (err) {
			console.error(err)
			notify({ group: 'error', text: 'Failed to update audio sync' })
		}
	}, 200)

	const setVolume = (media, volume) => {
		if (!flow.value || audioBlocked(media)) return
		const flowId = flow.value._id
		store.dispatch('Mediaflows/updateFlowMediaVolume', { mediaflowId: mediaflow.value._id, flowId, mediaId: media.id, volume })
		saveVolume(mediaflow.value._id, flowId, media.id, volume)
	}

	const toggleMute = async (media) => {
		if (!flow.value || audioBlocked(media)) return
		const flowId = flow.value._id
		const muted = !(media?.muted ?? false)
		try {
			await MediaflowService.muteFlowMedia(mediaflow.value._id, flowId, media.id, muted)
			store.dispatch('Mediaflows/updateFlowMediaMuted', { mediaflowId: mediaflow.value._id, flowId, mediaId: media.id, muted })
			told()
		} catch (err) {
			console.error(err)
			notify({ group: 'error', text: 'Failed to toggle mute' })
		}
	}

	const setAudioSync = (media, offset) => {
		if (!flow.value || audioBlocked(media)) return
		saveAudioSync(mediaflow.value._id, flow.value._id, media.id, offset)
	}

	return { reorder, toggleShow, setVolume, toggleMute, setAudioSync, audioBlocked }
}
